<!--

@@author        j0Shi
@@date          2019.08.20
@@desc          Mobile booking band on bottom of the object page
@@usedby        Object

-->

<template>
  <div class="booking-band">
    <div
      class="booking-band-body"
      :class="{ 'no-vacancy': !hasObjectVacancy }"
    >
      <object-vacancy-form-status
        v-if="!hasObjectVacancy"
        :error="!hasObjectVacancy"
      />
      <div
        v-else
        class="row row-tight"
      >
        <div class="col-6 col-md-8">
          <div v-if="priceData.status === 'occupied'">
            <div class="price-min price price-occupied">
              <em>{{ $t('object:occupied') }}</em>
              <div class="price-period">
                {{ $t('object:onSelectedDate') }}
              </div>
            </div>
          </div>
          <object-price
            v-else
            :price-min="objectMinPrice"
            :data="priceData"
          />
          <button
            v-show="startEndDateString || fetchingPriceError"
            type="button"
            class="btn btn-link btn-link-date py-0"
            data-ident="openModalPickr"
            @click="openModalPickr()"
          >
            <template v-if="priceData.status === 'occupied'">
              <i class="icon booking-band-calendar-icon-occupied"><icon-calendar-times /></i>
            </template>
            <template v-else>
              <i class="icon booking-band-calendar-icon-color"><icon-calendar-alt /></i>
            </template>
            <span class="booking-band-dates-color">
              {{ startEndDateString }}
            </span>
          </button>
        </div>
        <div class="col-6 col-md-4">
          <object-vacancy-buttons
            :params="queryParams"
            :data="priceData"
            :has-vacancy-data="hasObjectVacancy"
            data-ident="openVacancyModalPickr"
            @click-pick-date="openModalPickr()"
          />
          <!-- eslint-disable-next-line vuejs-accessibility/anchor-has-content -->
          <a
            v-smooth-scroll="{ duration: 200 }"
            target="_self"
            href="#bewertungen"
          >
            <rating-stars-canvas
              v-if="objectRatingCount"
              :rating-count="objectRatingCount"
              :average="objectRatingAverage"
              :show-average="true"
            />
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import RatingStarsCanvas from 'components/RatingStarsCanvas.vue';
import ObjectPrice from 'components/ObjectPrice.vue';
import ObjectVacancyButtons from 'components/ObjectVacancyButtons.vue';
import ActiveObjectStore from 'mixins/ActiveObjectStore';
import { fromAPIDateString, toDateString } from 'utils/DateHelper';
import ObjectVacancyFormStatus from 'components/ObjectVacancyFormStatus.vue';

/* ICONS */
import IconCalendarAlt from 'ICON:IconCalendarAlt';
import IconCalendarTimes from 'ICON:IconCalendarTimes';

export default {
  name: 'BookingBand',
  components: {
    RatingStarsCanvas,
    ObjectPrice,
    ObjectVacancyButtons,
    IconCalendarAlt,
    IconCalendarTimes,
    ObjectVacancyFormStatus,
  },
  mixins: [
    // store helper with necessary getters to deal with active objects
    ActiveObjectStore,
  ],
  i18nOptions: { namespaces: ['errors', 'object'] },
  props: {
    // vacancy data for the picker
    pickerVacancyData: {
      type: Object,
      default: () => ({
        count: 0,
        days: {},
        maxDate: new Date(),
      }),
    },
  },
  computed: {
    objectRatingAverage() { return this.getObjectDataByKey('ratingAverage'); },
    objectRatingCount() { return this.getObjectDataByKey('ratingCount'); },
    objectMinPrice() { return this.getObjectDataByKey('minPrice'); },
    // @vuese
    // price data from the store
    priceData() {
      return this.$store.getters.getPriceByKey(this.$store.state.objects.priceRequestData.identifierKey);
    },
    // @vuese
    // query param object that holds the params for api queries
    queryParams() {
      return {
        startDate: this.priceData.priceObject.startDate || undefined,
        endDate: this.priceData.priceObject.endDate || undefined,
        persons: parseInt(this.priceData.priceObject.persons, 10),
      };
    },
    startEndDateString() {
      let startDate = this.priceData.priceObject.startDate || undefined;
      startDate = startDate ? toDateString(fromAPIDateString(startDate), 'dd.MM.yy') : '';
      let endDate = this.priceData.priceObject.endDate || undefined;
      endDate = endDate ? toDateString(fromAPIDateString(endDate), 'dd.MM.yy') : '';

      let startEndDate = '';
      if (startDate && endDate) {
        startEndDate = `${startDate} - ${endDate}`;
      }

      return startEndDate;
    },
    // @vuese
    // boolean marker indicating that something went wrong with the price request
    fetchingPriceError() {
      return this.$store.getters.getFetchingPriceError;
    },
  },
  methods: {
    // trigger vuex action for opening modal pickr
    openModalPickr() {
      this.$store.dispatch('OPEN_MODAL_PICKR', true);
    },
  },
};
</script>

<style lang="scss">
  .b-toaster { z-index: 2004; }
  .btn-link-date {
    @include font_size_s;
  }
</style>

<style lang="scss" scoped>
  .booking-band .booking-band-body.no-vacancy {
    padding: 0;

    /deep/ div.vacancy-status {
      margin: 0;
      transform: none;

      div.bubble.bubble-danger {
        margin: 0;
        padding: $half_margin_content $margin_content;
      }
    }
  }

  /deep/ .vacancy-dates,
  /deep/ .vacancy-period {
    @include font_size_s;
  }

  /deep/ .object-price {
    flex-direction: column;

    * {
      text-align: left;
    }

    .price-reduction {
      justify-content: flex-start;
      del {
        order: 2;
        margin-left: .5em;
      }
      .badge-price-reduction {
        margin-left: 0;
      }
    }
  }

  .price-occupied {
    em {
      margin: 0;
    }
  }
</style>
